import { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Theme } from '@/styles/theme';
import { GapProps } from '@/types/ui';
import { getSizeStyle } from '@/utils/style/ui';
import Grid from '../Grid';

interface StyleProps {
  gap?: GapProps;
  width?: number;
  height: number;
  bg: keyof Theme['bg'];
  buttonBg: keyof Theme['bg'];
  shadow: keyof Theme['shadow'];
}

export interface Props extends Partial<StyleProps> {
  isReadonly?: boolean;
  value?: number;
  items?: string[];
  renderItem?: (value: string, isActive?: boolean) => React.ReactNode;
  onChange?: (index: number, value: string) => void;
}

const Container = styled(Grid)`
  position: absolute;
  top: 0px;
  bottom: 0px;
  left: 0px;
  right: 0px;
`;
const TabContainer = styled(Container)``;
const Tab = styled.div<{ value: number; gap: GapProps }>`
  ${({ theme }) => theme.rounded.full};
  ${({ theme }) => theme.transition.default}
  transform: translateX(calc(${({ theme, value, gap }) =>
    `${value * 100}% + (${getSizeStyle({ theme, size: gap }) || theme.gap.xxs} * ${value})`}));
`;
const ButtonContainer = styled(Container)``;
const Button = styled.button<{ isReadonly?: boolean; isActive?: boolean }>`
  ${({ theme }) => theme.text.md}
  ${({ theme }) => theme.font.bold}
  color: ${({ theme, isActive }) =>
    isActive ? theme.colors.text : theme.colors.secondary};
  text-align: center;
  ${({ theme }) => theme.rounded.full};
  cursor: ${({ isReadonly, isActive }) =>
    isReadonly || isActive ? 'default' : 'pointer'};
  ${({ theme }) => theme.transition.default}

  &:hover {
    ${({ theme, isReadonly, isActive }) =>
      isReadonly || isActive ? '' : theme.bg.surfaceHover}
  }
`;
const Root = styled.div<StyleProps>`
  position: relative;
  ${({ width }) => (width ? `width: ${width}px;` : '')}
  height: ${({ height }) => height}px;
  ${({ theme, gap }) =>
    getSizeStyle({ theme, size: gap, key: 'gap' }) || theme.gap.xxs}
  ${({ theme, bg }) => theme.bg[bg]};
  ${({ theme }) => theme.rounded.full};

  ${Tab} {
    ${({ theme, buttonBg }) => theme.bg[buttonBg]};
    ${({ theme, shadow }) => theme.shadow[shadow]};
  }
`;

export default function ButtonTab({
  isReadonly,
  value,
  gap = 'xxs',
  width,
  height = 64,
  bg = 'background',
  buttonBg = 'surface',
  shadow = 'innerCard',
  items,
  renderItem,
  onChange,
}: Props) {
  // state
  const [innerValue, setInnerValue] = useState(0);

  // memo
  const templateColumns = useMemo(
    () => items?.map(() => '1fr')?.join(' '),
    [items]
  );
  const isUsingProps = useMemo(() => value !== undefined, [value]);
  const usingValue = useMemo(
    () => (isUsingProps ? value : innerValue),
    [innerValue, value, isUsingProps]
  );

  // callback
  const handleClick = useCallback(
    (index: number, value: string) => () =>
      isReadonly
        ? undefined
        : usingValue === index
          ? undefined
          : isUsingProps
            ? onChange?.(index, value)
            : setInnerValue(index),
    [isReadonly, isUsingProps, onChange, usingValue]
  );

  return (
    <Root
      width={width}
      height={height}
      bg={bg}
      buttonBg={buttonBg}
      shadow={shadow}
    >
      <TabContainer
        $templateColumns={templateColumns}
        $gap={gap}
        $padding={gap}
      >
        {items?.length && usingValue !== undefined ? (
          <Tab gap={gap} value={usingValue} />
        ) : (
          ''
        )}
      </TabContainer>
      <ButtonContainer
        $templateColumns={templateColumns}
        $gap={gap}
        $padding={gap}
      >
        {items?.map((value, index) => (
          <Button
            isReadonly={isReadonly}
            isActive={usingValue === index}
            onClick={handleClick(index, value)}
            key={index}
          >
            {renderItem ? renderItem(value, usingValue === index) : value}
          </Button>
        ))}
      </ButtonContainer>
    </Root>
  );
}
